import { open, seal } from "@codeshare/crypto";
import type { JoinOptions, SignalPayload, Unsubscribe, WireMessage } from "./types";

type PeerSeen = (peerId: string) => void;
type Signal = (from: string, payload: SignalPayload) => void;

// Unknown kinds are dropped, so new ones (like "full") are safe to send to older builds.
const KINDS = new Set(["announce", "sdp", "ice", "full", "relay"]);

function isPayload(value: unknown): value is SignalPayload {
  return (
    typeof value === "object" &&
    value !== null &&
    KINDS.has((value as { kind?: string }).kind ?? "")
  );
}

/**
 * The adapter-independent part of signaling: sealing/opening payloads, ignoring anything that
 * isn't for us, and peer discovery. Adapters only move `WireMessage`s.
 *
 * Discovery: a joining peer broadcasts `announce`; everyone who hasn't seen it replies with a
 * directed `announce`, so peers find each other regardless of join order.
 */
export class SignalingCore {
  readonly #publish: (message: WireMessage) => Promise<void>;
  readonly #seen = new Set<string>();
  readonly #peerSeen = new Set<PeerSeen>();
  readonly #signal = new Set<Signal>();
  #options: JoinOptions | undefined;
  /** Decryption is async; chaining keeps callbacks in arrival order (SDP before its ICE). */
  #receiving: Promise<void> = Promise.resolve();

  constructor(publish: (message: WireMessage) => Promise<void>) {
    this.#publish = publish;
  }

  get joined(): JoinOptions | undefined {
    return this.#options;
  }

  start(options: JoinOptions): void {
    this.#options = options;
    this.#seen.clear();
  }

  stop(): void {
    this.#options = undefined;
    this.#seen.clear();
  }

  onPeerSeen(callback: PeerSeen): Unsubscribe {
    this.#peerSeen.add(callback);
    return () => this.#peerSeen.delete(callback);
  }

  onSignal(callback: Signal): Unsubscribe {
    this.#signal.add(callback);
    return () => this.#signal.delete(callback);
  }

  /** Broadcasts our presence (on join, and periodically for adapters that need it). */
  announce(): Promise<void> {
    return this.#send(undefined, { kind: "announce" });
  }

  send(to: string, payload: SignalPayload): Promise<void> {
    return this.#send(to, payload);
  }

  forget(peerId: string): void {
    this.#seen.delete(peerId);
  }

  /**
   * Handles a message an adapter received, strictly in arrival order. Malformed, foreign, or
   * undecryptable input is dropped.
   */
  receive(message: WireMessage): Promise<void> {
    this.#receiving = this.#receiving.then(() => this.#process(message));
    return this.#receiving;
  }

  async #process(message: WireMessage): Promise<void> {
    const options = this.#options;
    if (!options || message.from === options.selfId) return;
    if (message.to !== undefined && message.to !== options.selfId) return;

    let payload: unknown;
    try {
      payload = JSON.parse(await open(options.sigKey, message.sealed));
    } catch {
      return; // another room on a colliding topic, tampering, or garbage
    }
    if (this.#options !== options || !isPayload(payload)) return;

    if (!this.#seen.has(message.from)) {
      this.#seen.add(message.from);
      // Answer a broadcast announce so a newcomer learns about us too.
      if (payload.kind === "announce" && message.to === undefined) {
        void this.#send(message.from, { kind: "announce" }).catch(() => {});
      }
      for (const callback of this.#peerSeen) callback(message.from);
    }
    if (payload.kind !== "announce") {
      for (const callback of this.#signal) callback(message.from, payload);
    }
  }

  async #send(to: string | undefined, payload: SignalPayload): Promise<void> {
    const options = this.#options;
    if (!options) throw new Error("signaling: not joined");
    const sealed = await seal(options.sigKey, JSON.stringify(payload));
    await this.#publish(
      to === undefined ? { from: options.selfId, sealed } : { from: options.selfId, to, sealed },
    );
  }
}
